@if (!editMode) {
  <div class="table-shell">
    <mat-card appearance="outlined" class="table-card">
      <div class="input-label">
        <strong>{{ table.type }}</strong>
        <span class="input-label-summary">{{ inputLabel }}</span>
      </div>
      <hr />
      <mat-card-content>
        @if (table.matrixTable) {
          <app-table-matrix [table]="table" [editMode]="false" />
        } @else if (!table.rangeTest) {
          @if (has1dTableInput && table.lookupTests?.length) {
            <table class="table" style="width: auto">
              <thead>
                <tr>
                  <th scope="col">Lookup Value</th>
                  <th scope="col">Amount</th>
                </tr>
              </thead>
              <tbody>
                @for (test of table.lookupTests; track test) {
                  <tr>
                    <th scope="row">{{ test.lookupValue }}</th>
                    <td>{{ test.result | number }}</td>
                  </tr>
                }
              </tbody>
            </table>
          } @else {
            <p class="table-placeholder-no-grid">This table has no input configured yet, or lookup rows are not built.</p>
          }
        } @else {
          @if (has1dTableInput && table.rangeTests?.length) {
            <table class="table" style="width: auto">
              <thead>
                <tr>
                  <th scope="col">Start</th>
                  <th scope="col">End</th>
                  <th scope="col">Amount</th>
                </tr>
              </thead>
              <tbody>
                @for (test of table.rangeTests; track test) {
                  <tr>
                    <td>{{ test.rangeStart | number }}</td>
                    <td>{{ test.rangeFinish | number }}</td>
                    <td>{{ test.result | number }}</td>
                  </tr>
                }
              </tbody>
            </table>
          } @else {
            <p class="table-placeholder-no-grid">This table has no input configured yet, or no range rows.</p>
          }
        }
      </mat-card-content>
    </mat-card>
    @if (showActions) {
      <div class="table-save-bar" role="group" aria-label="Table actions">
        <button mat-flat-button color="primary" type="button" (click)="edit.emit()">Edit Table</button>
        <button mat-stroked-button color="warn" type="button" (click)="delete.emit()" [disabled]="isNew">Delete Table</button>
      </div>
    }
  </div>
}

@if (editMode) {
  <div class="table-shell">
    <mat-card appearance="outlined" class="table-card">
      <mat-card-header class="table-card-header">
        <mat-card-title><h2>Edit Table Information</h2></mat-card-title>
      </mat-card-header>
      <mat-form-field class="table-edit-field table-edit-field-name">
        <mat-label>Name</mat-label>
        <input matInput placeholder="Name" type="text" [value]="table.name" [(ngModel)]="table.name" />
      </mat-form-field>
      <mat-form-field class="table-edit-field table-edit-field-description">
        <mat-label>Description</mat-label>
        <input matInput placeholder="Description" type="text" [value]="table.description" [(ngModel)]="table.description" />
      </mat-form-field>

      @if (isMatrixTable) {
        <app-table-matrix
          [table]="table"
          [editMode]="true"
          [openRowColumnInputStepOnce]="openRowColumnInputStepOnce"
          (openRowColumnInputStepConsumed)="openRowColumnInputStepConsumed.emit()"
          (matrixModelChanged)="refreshInputLabel()"
          (rowColumnInputEditActive)="onMatrixRowColumnInputEditActive($event)"
        />
      } @else {
        @if (!has1dTableInput && !changeStatus) {
          <mat-card-content>
            <p class="table-placeholder-edit">Choose an input for this table. Changing the input later will clear all amounts.</p>
            <button mat-stroked-button color="primary" type="button" (click)="changeInput('edit')">Select table input</button>
          </mat-card-content>
        }
        @if ((!has1dTableInput && changeStatus) || (has1dTableInput && changeStatus)) {
          <mat-card-content>
            <input-selector [key]="table" [caller]="'Table'"></input-selector>
            <div class="table-inline-actions">
              <button mat-stroked-button color="primary" type="button" (click)="changeInput('save')" [disabled]="!has1dTableInput">Apply table input</button>
              <button mat-stroked-button color="warn" type="button" (click)="changeInput('cancel')">Cancel input changes</button>
            </div>
          </mat-card-content>
        }
        @if (has1dTableInput && !changeStatus) {
          <mat-card-content>
            <div class="table-input-header">
              <p class="table-input-question">
                <strong class="table-input-question-type">{{ table.type }}</strong>
                <span class="table-input-question-text">{{ inputLabel }}</span>
              </p>
              <div class="table-change-input-actions">
                <span class="table-change-input-warning">Changing the input will erase all of your amounts</span>
                <button mat-stroked-button color="primary" class="change-input-button" type="button" (click)="changeInput('edit')">Change table input</button>
              </div>
            </div>
            @if (!table.rangeTest) {
              <table class="table table-edit" style="width: auto">
                <thead>
                  <tr>
                    <th scope="col">Lookup Value</th>
                    <th scope="col" [attr.id]="lookupAmountColumnId">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  @for (test of table.lookupTests; track test; let i = $index) {
                    <tr>
                      <th scope="row" [attr.id]="getRowHeaderId('lookup', i)">{{ test.lookupValue }}</th>
                      <td>
                        <mat-form-field appearance="outline" subscriptSizing="dynamic" class="table-amount-field">
                          <mat-label>Amount</mat-label>
                          <input matInput type="number" [value]="test.result" [(ngModel)]="test.result" [attr.aria-labelledby]="lookupAmountColumnId + ' ' + getRowHeaderId('lookup', i)" />
                        </mat-form-field>
                      </td>
                    </tr>
                  }
                </tbody>
              </table>
            } @else {
              <span id="rangeDeleteRowLabel" class="cdk-visually-hidden">Delete row</span>
              <table class="table table-edit" style="width: auto">
                <thead>
                  <tr>
                    <th scope="col">Start</th>
                    <th scope="col" [attr.id]="rangeEndColumnId">End</th>
                    <th scope="col" [attr.id]="rangeAmountColumnId">Amount</th>
                    <td style="border: none"></td>
                  </tr>
                </thead>
                <tbody>
                  @for (test of table.rangeTests; track test; let i = $index) {
                    <tr>
                      <th scope="row" [attr.id]="getRowHeaderId('range', i)">{{ test.rangeStart | number }}</th>
                      <td>
                        <mat-form-field appearance="outline" subscriptSizing="dynamic" class="table-amount-field">
                          <mat-label>End</mat-label>
                          <input matInput type="number" [value]="test.rangeFinish" [(ngModel)]="test.rangeFinish" (change)="calcRanges(table.rangeTests)" [attr.aria-labelledby]="rangeEndColumnId + ' ' + getRowHeaderId('range', i)" />
                          @if (test.rangeFinish < test.rangeStart) {
                            <mat-hint> Amount too low </mat-hint>
                          }
                        </mat-form-field>
                      </td>
                      <td>
                        <mat-form-field appearance="outline" subscriptSizing="dynamic" class="table-amount-field">
                          <mat-label>Amount</mat-label>
                          <input matInput type="number" [value]="test.result" [(ngModel)]="test.result" [attr.aria-labelledby]="rangeAmountColumnId + ' ' + getRowHeaderId('range', i)" />
                        </mat-form-field>
                      </td>
                      <td>
                        <button mat-button color="warn" type="button" (click)="deleteRow(table.rangeTests, i)" [attr.aria-labelledby]="'rangeDeleteRowLabel ' + getRowHeaderId('range', i)">delete row</button>
                        @if (i === table.rangeTests.length - 1) {
                          <button mat-button color="secondary" type="button" (click)="addRow(table.rangeTests)">add row</button>
                        }
                      </td>
                    </tr>
                  }
                  @if (table.rangeTests === undefined) {
                    <tr>
                      <td>
                        <button mat-button color="secondary" type="button" (click)="addRow(table.rangeTests)">add row</button>
                      </td>
                    </tr>
                  }
                </tbody>
              </table>
            }
          </mat-card-content>
        }
      }
    </mat-card>
    @if (!matrixRowColumnInputStepOpen) {
      <div class="table-save-bar" role="group" aria-label="Save or discard table edits">
        <button mat-button type="button" (click)="cancelEdit.emit()">Cancel</button>
        @if (!this.changeStatus) {
          <button mat-flat-button color="primary" type="button" (click)="saveTable.emit()" [disabled]="!editMode">Save Table</button>
        }
      </div>
    }
  </div>
}
